import React, { FC, useCallback, useState, KeyboardEvent } from "react";
import { makeStyles, MenuItem, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";

import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  SearchCompany,
  SearchCompany_searchCompany,
  SearchCompanyVariables,
} from "../../../graphql/onboarding/corporate/types/SearchCompany";
import { SEARCH_COMPANY } from "../../../graphql/onboarding/corporate/mutation";
import { TARGET_SEARCH } from "../../../enums/pappers";
import { CustomButton } from "../../Ui/CustomButton";
import Layout from "../../Common/NewLayout";
import Header from "../../Common/NewLayout/Header";
import CustomSearchField from "../../Ui/CustomSearch";

const useStyles = makeStyles((theme) => ({
  errorMessage: {
    fontSize: ".85rem",
    lineHeight: 1.5,
    maxWidth: 450,
    whiteSpace: "normal", // Autorise le retour à la ligne
    overflowWrap: "break-word",
    color: "#4e5d78",
  },
  listItem: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "450px",
    maxHeight: "calc(100vh - 375px)",
    overflowY: "auto",
  },
  item: {
    color: "#4e5d78",
    fontSize: ".85rem",
  },
}));

interface CompanySearchProps {
  incorporationCode?: string;
}

const CompanySearch: FC<CompanySearchProps> = ({ incorporationCode = "" }) => {
  const navigate = useNavigate(); // Hook de navigation
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState(incorporationCode);
  const [listCompany, setListCompany] = useState<
    (SearchCompany_searchCompany | null)[]
  >([]);
  const [isEmptySearch, setIsEmptySearch] = useState(false);
  const [infoSearch, setInfoSearch] = useState(incorporationCode);
  const [displayManually, setDisplayManually] = useState(false);

  const [search, { loading }] = useMutation<
    SearchCompany,
    SearchCompanyVariables
  >(SEARCH_COMPANY, {
    onCompleted: (data) => {
      const companies = data?.searchCompany || [];
      setListCompany(companies);
      setIsEmptySearch(companies.length === 0);
      setDisplayManually(true);
    },
    onError: () => {
      setDisplayManually(true);
    },
  });

  const searchCompanyList = useCallback(
    (value: string) => {
      search({
        variables: {
          search: value,
          length: 100,
          targets: [
            TARGET_SEARCH.FULL_NAME,
            TARGET_SEARCH.NAME_COMPANY,
            TARGET_SEARCH.DENOMINATION,
            TARGET_SEARCH.SIREN,
            TARGET_SEARCH.SIRET,
          ],
        },
      });
    },
    [search],
  );

  const handlePrevious = () => {
    return navigate("/offer");
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (value) {
        searchCompanyList(value);
      }
    },
    [searchCompanyList],
  );

  const handleNoCompany = () => {
    navigate(`/companyInfo/create`);
  };

  const handleSelectCompany = (
    selectedCompany: SearchCompany_searchCompany | null,
  ) => {
    navigate(`/companyInfo/${selectedCompany?.incorporationCode}`);
    return null;
  };

  const onSearchClicked = () => {
    handleSearch(infoSearch);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInfoSearch(value);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSearchClicked();
    }
  };

  return (
    <Layout
      title="Informations"
      description="Saisissez votre SIRET / SIREN ou votre raison sociale pour retrouver rapidement votre société."
    >
      <Header
        title="Recherchez votre entreprise"
        description="Entrez votre SIREN / SIRET / RAISON SOCIALE"
      />
      <Box onKeyPress={handleKeyPress}>
        <CustomSearchField
          name="search"
          onChange={handleSearchChange}
          onSearchClicked={onSearchClicked}
          iconPosition="end"
          searchValue={searchValue}
          placeholder="SIREN..."
        />
        <Box className={classes.listItem}>
          {listCompany?.map((company) => (
            <Box
              onClick={() => handleSelectCompany(company)}
              key={company?.incorporationCode}
            >
              <MenuItem
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography className={classes.item}>
                  {company?.name}
                </Typography>
                <Typography
                  className={classes.item}
                >{`${company?.incorporationCode} / ${company?.address}`}</Typography>
              </MenuItem>
            </Box>
          ))}
        </Box>
        {isEmptySearch && (
          <Typography className={classes.errorMessage}>
            <FormattedMessage id="company.search.message.empty" />
          </Typography>
        )}
        <Box marginTop={1}>
          <CustomButton
            type="button"
            variant="outline4"
            onClick={handlePrevious}
            label="button.previous"
            disabled={loading}
            fullWidthMobile
            fullWidth={!displayManually}
          />
          {displayManually && (
            <CustomButton
              type="button"
              variant="primary"
              onClick={handleNoCompany}
              label="company.redirect.empty"
              loading={loading}
              fullWidthMobile
            />
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default CompanySearch;
